import React, { useState, useRef, useCallback } from 'react';
import Receipt from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { TextFieldProps, useNotify, useRecordContext, useTranslate } from 'react-admin';
import { getToken } from '../authentication/authProvider';
import { PDFFormEditor, usePDFEditor } from '../components/PDFFormEditor';

const PAPERMAN_API_URL = process.env.REACT_APP_PAPERMAN_API_URL;

export type PaymentReceiptButtonProps = TextFieldProps & {
  source: string;
  funeralId?: number | string;
};

const PaymentReceiptButton = (props: PaymentReceiptButtonProps) => {
  const record = useRecordContext();
  const translate = useTranslate();
  const notify = useNotify();
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  
  // PDF editor hook
  const { handlePdfReady, downloadFlattenedPdf } = usePDFEditor();
  
  // Store the deceased name for the filename
  const [deceasedName, setDeceasedName] = useState<string>('');

  const handleOpenModal = () => {
    if (!props.funeralId) {
      notify('Funeral ID not available', { type: 'error' });
      return;
    }
    setModalOpen(true);
    setLoading(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setLoading(false);
  };

  const handlePdfLoaded = useCallback((bytes: Uint8Array) => {
    setLoading(false);
    handlePdfReady(bytes);
  }, [handlePdfReady]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const filename = deceasedName 
        ? `RRQ_Recu_${deceasedName.replace(/\s+/g, '_')}.pdf`
        : 'RRQ_Recu.pdf';
      await downloadFlattenedPdf(filename);
      notify('PDF downloaded successfully', { type: 'success' });
    } catch (error) {
      console.error('Download error:', error);
      notify('Failed to download PDF', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  // Legacy receipt download (original functionality)
  const handleLegacyReceipt = async () => {
    if (!record) return;
    
    try {
      const result = await fetch(
        `${PAPERMAN_API_URL}payments/en/${record[props.source]}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/pdf',
            'Content-Type': 'application/pdf',
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      );
      if (result.status >= 200 && result.status < 300) {
        const blob = await result.blob();
        window.open(URL.createObjectURL(blob));
      } else {
        notify(translate('resources.invoices.pdf_error'), { type: 'error' });
      }
    } catch (e: any) {
      console.warn(e);
      notify(e.message ?? JSON.stringify(e), { type: 'error' });
    }
  };

  if (!record) return null;

  // If no funeralId is provided, fall back to legacy behavior
  if (!props.funeralId) {
    return (
      <Button
        onClick={handleLegacyReceipt}
        startIcon={<Receipt />}
        aria-label={translate('resources.payments.receipt')}
      >
        {translate('resources.payments.receipt', { smart_count: 1 })}
      </Button>
    );
  }

  const pdfUrl = `${PAPERMAN_API_URL}proof-of-payment/${props.funeralId}`;

  return (
    <>
      <Button
        onClick={handleOpenModal}
        startIcon={<Receipt />}
        aria-label="Proof of Payment"
      >
        Proof of Payment
      </Button>

      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Proof of Payment - Edit & Download</Typography>
            <Typography variant="body2" color="text.secondary">
              Edit fields directly on the PDF, then download
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
          
          <Box sx={{ flexGrow: 1, overflow: 'auto', display: loading ? 'none' : 'block' }}>
            <PDFFormEditor
              pdfUrl={pdfUrl}
              onPdfReady={handlePdfLoaded}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseModal} disabled={downloading}>
            Close
          </Button>
          <Button
            onClick={handleDownload}
            variant="contained"
            color="primary"
            disabled={loading || downloading}
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
          >
            {downloading ? 'Downloading...' : 'Download PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentReceiptButton;
